/**
 * The 3D view's two drag tools. Rotate grabs the tip of the out-of-screen
 * vector; pan grabs the plan and slides it under the camera. Click-drag and
 * trackpad scroll perform the selected tool's motion, and holding shift or
 * dragging with the right or middle mouse button performs the other's, so a
 * touch screen still reaches both through the tool buttons. Held keys move
 * the camera too (`HeldKeyTracker`).
 */
export type View3DTool = 'rotate' | 'pan';

export const VIEW3D_TOOLS: readonly { id: View3DTool; label: string }[] = [
  { id: 'rotate', label: 'Rotate' },
  { id: 'pan', label: 'Pan' },
];

export const otherTool = (tool: View3DTool): View3DTool => tool === 'pan' ? 'rotate' : 'pan';

/** Which way the held keys move the camera: each axis -1, 0, or 1. */
export interface KeyMotion {
  right: number;
  forward: number;
  up: number;
}

/** The keys 3D programs move the camera with, by physical key (`KeyboardEvent.code`): W/S forward and back, A/D left and right, Q/E down and up. */
const MOTION_KEYS = new Map<string, Partial<KeyMotion>>([
  ['KeyW', { forward: 1 }],
  ['KeyS', { forward: -1 }],
  ['KeyD', { right: 1 }],
  ['KeyA', { right: -1 }],
  ['KeyE', { up: 1 }],
  ['KeyQ', { up: -1 }],
]);

/** What the tracker reads from a key press: the physical key, what it types (Cmd itself reads 'Meta'), and the modifiers held. */
export type TrackedKeyPress = Pick<KeyboardEvent, 'code' | 'key' | 'metaKey' | 'ctrlKey' | 'altKey'>;

/**
 * The camera keys held down, tracked by physical key, since what a key types
 * changes with the modifiers: D released with Option down reports '∂'. A
 * press with Cmd, Ctrl or Option held is a shortcut, not a camera move. macOS
 * reports no key coming up while Cmd is held, so Cmd going down lets go of
 * every key; so does `releaseAll`, for a window that loses focus and hears no
 * more of the keyboard.
 */
export class HeldKeyTracker {
  private heldCodes = new Set<string>();

  /** A key went down; true when it is a camera key, held from now on. */
  keyDown({ code, key, metaKey, ctrlKey, altKey }: TrackedKeyPress): boolean {
    if (key === 'Meta') this.heldCodes.clear();
    if (metaKey || ctrlKey || altKey || !MOTION_KEYS.has(code)) return false;
    this.heldCodes.add(code);
    return true;
  }

  keyUp({ code }: Pick<KeyboardEvent, 'code'>): void {
    this.heldCodes.delete(code);
  }

  releaseAll(): void {
    this.heldCodes.clear();
  }

  /** The motion the held keys add up to (opposite keys cancel); null when they move nothing. */
  get motion(): KeyMotion | null {
    const motion: KeyMotion = { right: 0, forward: 0, up: 0 };
    for (const code of this.heldCodes) {
      const { right = 0, forward = 0, up = 0 } = MOTION_KEYS.get(code) ?? {};
      motion.right += right;
      motion.forward += forward;
      motion.up += up;
    }
    return motion.right || motion.forward || motion.up ? motion : null;
  }
}

/** The tool a gesture performs: the selected one, or the other while shift is held. */
export const gestureTool = (selected: View3DTool, shiftKey: boolean): View3DTool => shiftKey ? otherTool(selected) : selected;
